/**
 * Modified: Await VariantSelectedEvent.promise (generation-gated) instead of a fragile pendingLoads refcount,
 * so aborted/superseded fetches cannot leave the ATC shimmer stuck. Media dim stays until the
 * visible gallery image has loaded, not only until the section HTML lands.
 */
import { ThemeEvents } from '@theme/events';
import { settleWithin } from '@theme/utilities';

// Stop price/ATC shimmering well before the fetch's own 10s timeout — past a few seconds a
// persistent shimmer reads as broken. Purely cosmetic: the price and buttons still update
// when the response lands.
const VARIANT_LOADING_TIMEOUT = 4000;

// Extra wait after a 4s shimmer timeout so a slow section fetch can still keep the gallery
// dimmed until HTML arrives (picker aborts at 10s).
const VARIANT_HTML_GRACE_TIMEOUT = 6000;

// After HTML morph, wait for the visible image to decode so the dim does not lift on the old photo.
const MEDIA_DECODE_TIMEOUT = 2000;

/**
 * Coordinates loading visuals for the product information grid while variant HTML is fetched.
 * @extends {HTMLElement}
 */
class ProductVariantLoading extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number} */
  #loadingGeneration = 0;

  /** @type {HTMLElement | null} */
  #regionEl = null;

  connectedCallback() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    this.#regionEl = section.querySelector('[data-product-variant-loading-region]');
    if (!this.#regionEl) return;

    const { signal } = this.#abortController;

    section.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /**
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  #pickerShouldTriggerLoading(target) {
    if (!(target instanceof HTMLElement) || target.tagName !== 'VARIANT-PICKER') return false;
    if (target.closest('quick-add-dialog, product-card')) return false;
    if (target.dataset.templateProductMatch === 'true') return true;
    if (target.closest('featured-product-information')) return true;
    return false;
  }

  /**
   * @param {import('@theme/events').VariantSelectedEvent} event
   */
  #onVariantSelected = async (event) => {
    if (!this.#pickerShouldTriggerLoading(event.target)) return;

    const generation = ++this.#loadingGeneration;
    this.#setLoading(true);

    const htmlOutcome = await settleWithin(event.promise, VARIANT_LOADING_TIMEOUT);

    if (generation !== this.#loadingGeneration) return;

    if (htmlOutcome.status === 'timeout') {
      this.#setLoading(false, { media: true, shimmer: false });
      await settleWithin(event.promise, VARIANT_HTML_GRACE_TIMEOUT);

      if (generation !== this.#loadingGeneration) return;
    }

    // Gallery morph is sync on variant:update; slide-0 sync is a queued microtask.
    await Promise.resolve();
    await settleWithin(this.#visibleMediaReady(), MEDIA_DECODE_TIMEOUT);

    if (generation !== this.#loadingGeneration) return;

    this.#setLoading(false);
  };

  /**
   * Resolves when the currently visible PDP/featured gallery image has loaded (or cannot).
   * Do not use the first `aria-hidden="false"` slide: skip-morph `select()` used to leave the
   * previous slide at false until IntersectionObserver ran, which lifted the dim on the old photo.
   * @returns {Promise<void>}
   */
  async #visibleMediaReady() {
    const media = this.#regionEl?.querySelector('.product-information__media');
    if (!media) return;

    const img = visibleGalleryImage(media);

    if (!(img instanceof HTMLImageElement) || !img.src) return;

    if (img.loading === 'lazy') {
      img.loading = 'eager';
    }

    if (img.complete && img.naturalWidth > 0) {
      if (typeof img.decode === 'function') {
        try {
          await img.decode();
        } catch {
          // Broken or aborted decode — still drop the dim.
        }
      }

      return;
    }

    await new Promise((resolve) => {
      const done = () => resolve();
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }

  /**
   * @param {boolean} loading
   * @param {{ media?: boolean, shimmer?: boolean }} [parts]
   */
  #setLoading(loading, parts = {}) {
    const region = this.#regionEl;
    if (!region) return;

    const mediaDim = parts.media ?? loading;
    const shimmer = parts.shimmer ?? loading;

    if (mediaDim || shimmer) {
      region.setAttribute('aria-busy', 'true');
    } else {
      region.removeAttribute('aria-busy');
    }

    const media = region.querySelector('.product-information__media');
    media?.classList.toggle('product-variant-loading--media-dim', mediaDim);

    for (const price of region.querySelectorAll('product-price')) {
      price.classList.toggle('product-variant-loading--shimmer', shimmer);
    }

    // Always resolve against the live form buttons node — morphs can replace it while
    // a stuck shimmer class would leave ATC looking half-disabled without :disabled cursor.
    for (const buttons of region.querySelectorAll('.product-form-buttons')) {
      buttons.classList.toggle('product-variant-loading--shimmer', shimmer);
    }
  }
}

if (!customElements.get('product-variant-loading')) {
  customElements.define('product-variant-loading', ProductVariantLoading);
}

/**
 * Grid vs carousel is a `display` swap at 750px. Do not use `checkVisibility({ opacity })` —
 * the variant dim is on an ancestor and would hide the layout we are trying to wait on.
 * @param {Element | null | undefined} element
 * @returns {element is HTMLElement}
 */
function isLayoutDisplayed(element) {
  if (!(element instanceof HTMLElement)) return false;
  if (getComputedStyle(element).display === 'none') return false;

  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * @param {Element} media
 * @returns {HTMLImageElement | null}
 */
function visibleGalleryImage(media) {
  const gallery = media.querySelector('media-gallery');
  const grid = gallery?.querySelector('.media-gallery__grid');

  if (isLayoutDisplayed(grid)) {
    const img = grid.querySelector('img.product-media__image');
    if (img instanceof HTMLImageElement) return img;
  }

  const slideshow = gallery?.querySelector('slideshow-component');

  if (slideshow instanceof HTMLElement && 'current' in slideshow) {
    const host = /** @type {HTMLElement & { current: number, slides?: Element[] }} */ (slideshow);
    const { slides, current } = host;
    const slide = Array.isArray(slides) && Number.isFinite(current) ? slides[current] : null;
    const img = slide instanceof Element ? slide.querySelector('img.product-media__image') : null;
    if (img instanceof HTMLImageElement) return img;
  }

  const fallback =
    media.querySelector('slideshow-slide[aria-hidden="false"] img.product-media__image') ||
    media.querySelector('img.product-media__image');

  return fallback instanceof HTMLImageElement ? fallback : null;
}
